/**
 * KanbanCard - Card de conversa arrastável no Kanban
 * 
 * Exibe informações resumidas da conversa com suporte a drag & drop
 */

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { MessageSquare, Grip, Tag } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { getStorageUrl } from '@/lib/storage';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { getAvatarColorClasses } from '@/lib/utils/avatar-colors';
import { KanbanCardCrmSection } from './KanbanCardCrmSection';
import { ChannelBadge } from '@/components/tenant/shared/ChannelBadge';
import type { KanbanConversation } from '@/hooks/useKanban';
import type { ContactCrmData } from '@/hooks/useKanbanConversationCrmData';

/**
 * Trunca o preview da mensagem para evitar problemas de layout
 * Mesmo padrão usado no ConversationList (máximo 35 caracteres)
 */
const truncatePreview = (text: string | null, maxLength: number = 22): string => {
  if (!text) return 'Sem mensagens';
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
};

/**
 * Formata tempo de forma compacta (mesmo padrão do Chat)
 * Ex: 5s, 30s, 2 minutos, 1 hora
 */
const formatTime = (dateStr: string | null): string => {
  if (!dateStr) return '';
  
  const date = new Date(dateStr);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  
  // Se menos de 60 segundos, mostrar segundos
  if (diffInSeconds < 60) {
    return `${Math.max(1, diffInSeconds)}s`;
  }
  
  // Para tempos maiores, usar formatDistanceToNow sem "cerca de"
  const timeStr = formatDistanceToNow(date, { 
    addSuffix: false, 
    locale: ptBR 
  });
  return timeStr.replace(/^cerca de /i, '');
};

interface KanbanCardProps {
  conversation: KanbanConversation;
  /** Dados CRM do contato (deal + contrato), opcional */
  crmData?: ContactCrmData | null;
  /** Callback ao clicar na seção CRM - abre modal de deal */
  onCrmClick?: (dealId: string) => void;
  onClick?: () => void;
}

export function KanbanCard({ conversation, crmData, onCrmClick, onClick }: KanbanCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: conversation.id,
    data: {
      type: 'conversation',
      conversation,
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  // v1.2.0: Resolve a entidade exibida no card.
  // Quando a conversa é de grupo (group != null), prioriza nome/avatar do grupo.
  // Caso contrário, mantém o comportamento original baseado em contact.
  const isGroup = !!conversation.group;
  const displayName_raw =
    (isGroup ? conversation.group?.name : conversation.contact?.name)
    || (!isGroup ? conversation.contact?.phone : null)
    || 'Contato';
  const displayAvatarUrl = isGroup
    ? conversation.group?.avatar_url
    : conversation.contact?.avatar_url;

  // Extrai iniciais do nome (ou telefone, no caso de individual)
  const getInitials = () => {
    const name = displayName_raw;
    if (name === 'Contato') return '?';
    if (name.includes('@') || /^\d+$/.test(name.replace(/\D/g, ''))) {
      return name.substring(0, 2).toUpperCase();
    }
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  };

  // Nome de exibição (truncado em 15 caracteres)
  const displayName = displayName_raw.length > 15 ? displayName_raw.slice(0, 15) + '...' : displayName_raw;

  // Formata tempo compacto
  const timeAgo = formatTime(conversation.last_message_at);

  return (
    <Card
      ref={setNodeRef}
      style={style}
      className={cn(
        'p-3 cursor-pointer hover:shadow-md transition-all duration-200 relative',
        'border border-border/50 bg-card overflow-hidden',
        isDragging && 'opacity-50 shadow-lg rotate-2 scale-105'
      )}
      onClick={onClick}
    >
      {/* Layout principal */}
      <div className="flex items-start gap-2">
        {/* Avatar com badge do canal sobreposto (mesmo padrão de ConversationList e fila) */}
        <div className="relative shrink-0">
          <Avatar className="h-9 w-9">
            <AvatarImage src={getStorageUrl(displayAvatarUrl)} />
            <AvatarFallback className={cn("text-xs", getAvatarColorClasses(displayName_raw))}>
              {getInitials()}
            </AvatarFallback>
          </Avatar>
          <ChannelBadge
            channelType={conversation.inbox?.channel_type}
            providerSlug={conversation.inbox?.provider_slug}
          />
        </div>

        {/* Conteúdo */}
        <div className="flex-1 min-w-0 overflow-hidden">
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm text-foreground truncate flex-1">
              {displayName}
            </span>
            {/* Badge de não lidas no canto direito do nome */}
            {conversation.unread_count > 0 && (
              <Badge variant="default" className="h-5 min-w-[20px] px-1.5 text-xs shrink-0">
                {conversation.unread_count}
              </Badge>
            )}
          </div>
          
          {/* Preview da última mensagem */}
          <p className="text-xs text-muted-foreground truncate break-all mt-0.5">
            {truncatePreview(conversation.last_message_preview)}
          </p>
          
          {/* Rodapé: tempo, badges (esquerda) + avatar (direita fixa) */}
          <div className="flex items-center justify-between gap-2 mt-2">
            {/* Lado esquerdo: tempo + badges (com flex-wrap) */}
            <div className="flex items-center gap-1.5 flex-wrap flex-1 min-w-0">
              {timeAgo && (
                <span className="text-[10px] text-muted-foreground">{timeAgo}</span>
              )}
              {conversation.inbox?.name && (
                <Badge variant="outline" className="h-4 text-[10px] px-1.5 max-w-[80px]">
                  <MessageSquare className="h-2.5 w-2.5 mr-1 shrink-0" />
                  <span className="truncate">{conversation.inbox.name}</span>
                </Badge>
              )}
            </div>

            {/* Lado direito: Avatar do agente (fixo no canto) */}
            {conversation.assigned_agent && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="shrink-0">
                    <Avatar className="h-5 w-5 border border-green-500/50">
                      <AvatarImage 
                        src={getStorageUrl(conversation.assigned_agent.avatar_url)} 
                        alt={conversation.assigned_agent.full_name || 'Agente'} 
                      />
                      <AvatarFallback className="text-[8px] bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                        {(conversation.assigned_agent.full_name || 'AG').slice(0, 2).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="top" className="text-xs">
                  {conversation.assigned_agent.full_name || 'Agente'}
                </TooltipContent>
              </Tooltip>
            )}
          </div>
          
          {/* Tags do Contato */}
          {conversation.contact?.tags && conversation.contact.tags.length > 0 && (
            <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
              {conversation.contact.tags.slice(0, 2).map(tag => (
                <div 
                  key={tag.id}
                  className="flex items-center gap-0.5 text-[10px] font-medium"
                  style={{ color: tag.color }}
                >
                  <Tag size={10} />
                  <span className="max-w-[50px] truncate">{tag.name}</span>
                </div>
              ))}
              {conversation.contact.tags.length > 2 && (
                <Badge 
                  variant="secondary" 
                  className="text-[10px] px-1 py-0 h-4"
                  title={conversation.contact.tags.slice(2).map(t => t.name).join(', ')}
                >
                  +{conversation.contact.tags.length - 2}
                </Badge>
              )}
            </div>
          )}

          {/* Seção CRM: Deal + Contrato (visível apenas quando crmData presente) */}
          {crmData && (
            <KanbanCardCrmSection crmData={crmData} onCrmClick={onCrmClick} />
          )}
        </div>
      </div>

      {/* Handle de arraste no canto inferior esquerdo */}
      <div
        {...attributes}
        {...listeners}
        className="absolute bottom-2 left-2 cursor-grab active:cursor-grabbing text-muted-foreground/40 hover:text-muted-foreground"
      >
        <Grip className="h-4 w-4" />
      </div>
    </Card>
  );
}
